import { useRef, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useHistory } from 'react-router-dom';
import routes from '~/routes';
import { cloneCase, saveCaseRetryingDisjoint, StaleCaseError } from './api';
import { buildPayload } from './utils';
import type { CaseFormValues, CaseResponse } from './types';

/**
 * The case form's TanStack instance, from the loaded case's values.
 *
 * @param {CaseFormValues} initialValues - The values the form starts from.
 * @return {object} The form.
 */
export const useCaseForm = (initialValues: CaseFormValues) =>
  useForm({ defaultValues: initialValues });

/** The case form's TanStack instance type. */
export type CaseFormInstance = ReturnType<typeof useCaseForm>;

interface UseCaseSaveOptions {
  caseId: number | null;
  /** The values the form started from (the loaded case, or the defaults for a new one). */
  initialValues: CaseFormValues;
  loaded: CaseResponse | null;
  /** Whether the user has touched the form. */
  isTouched: boolean;
  /** The form's current values, read at save time. */
  getValues: () => CaseFormValues;
  onSaved: (saved: CaseResponse) => void;
  /** A save that failed for any reason but a stale case. */
  onError: (message: string) => void;
}

/**
 * The case form's save: what it diffs against, the updatedAt it sends, and the stale refusal.
 *
 * - The baseline (buildPayload diffs against it) is the values the form started from. An
 *   untouched form follows a reload (after an acknowledgement); a touched one keeps what it was
 *   shown, so a field the user left alone is never sent back over someone else's change.
 * - expectedUpdatedAt is the case's updatedAt as this form read it, sent so a save over a case
 *   written since is refused (409). It follows a reload while the form is untouched, and also once
 *   touched when the reload changed none of the form's fields (this user's own acknowledgement,
 *   which writes only review bookmarks): only someone else's change to a field makes it stale.
 * - A stale save the change since doesn't overlap is retried once (saveCaseRetryingDisjoint);
 *   otherwise `stale` is set and the edits stay on screen until the user reloads.
 *
 * @param {UseCaseSaveOptions} options - The case and the form's state.
 * @return {object} `saving`, `stale` and `save`.
 */
export function useCaseSave({
  caseId,
  initialValues,
  loaded,
  isTouched,
  getValues,
  onSaved,
  onError,
}: UseCaseSaveOptions) {
  const [saving, setSaving] = useState(false);
  const [stale, setStale] = useState(false);
  const baseline = useRef(initialValues);
  if (!isTouched) baseline.current = initialValues;
  const expectedUpdatedAt = useRef(loaded?.updatedAt ?? null);
  if (
    !isTouched ||
    Object.keys(buildPayload(initialValues, baseline.current).evaluation_case)
      .length === 0
  ) {
    expectedUpdatedAt.current = loaded?.updatedAt ?? null;
  }

  const save = async () => {
    setSaving(true);
    try {
      onSaved(
        await saveCaseRetryingDisjoint(
          getValues(),
          caseId,
          baseline.current,
          expectedUpdatedAt.current
        )
      );
    } catch (error) {
      if (error instanceof StaleCaseError) setStale(true);
      else
        onError(error instanceof Error ? error.message : 'Failed to save case');
    } finally {
      setSaving(false);
    }
  };

  return { saving, stale, save };
}

/**
 * Clone Case: clones the saved case and opens the copy, or reports why it failed.
 *
 * @param {number | null} caseId - The case, or null for a new one (nothing to clone).
 * @param {Function} onError - Reports a failed clone.
 * @return {object} `cloning` while in flight, and `clone`.
 */
export function useCaseClone(
  caseId: number | null,
  onError: (message: string) => void
) {
  const history = useHistory();
  const [cloning, setCloning] = useState(false);

  const clone = async () => {
    if (caseId === null) return;
    setCloning(true);
    try {
      const copy = await cloneCase(caseId);
      history.push(
        routes.evaluationCaseDetailPath.replace(':id', String(copy.id))
      );
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to clone case');
    } finally {
      setCloning(false);
    }
  };

  return { cloning, clone };
}
